import React, { useState, useEffect } from 'react';
import { digestAPI } from '@/lib/api';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Switch } from '@/components/ui/switch';
import { Label } from '@/components/ui/label';
import { toast } from 'sonner';
import { Mail, Send, Loader2, CalendarDays } from 'lucide-react';

const DAYS = ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday', 'Sunday'];

export function WeeklyDigestSettings() {
  const [config, setConfig] = useState({ enabled: true, day_of_week: 0 });
  const [loading, setLoading] = useState(true);
  const [sending, setSending] = useState(false);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    digestAPI.getConfig()
      .then(res => setConfig(res.data))
      .catch(() => {})
      .finally(() => setLoading(false));
  }, []);

  const handleSave = async () => {
    setSaving(true);
    try {
      await digestAPI.updateConfig({ enabled: config.enabled, day_of_week: config.day_of_week });
      toast.success('Digest preferences saved');
    } catch {
      toast.error('Failed to save preferences');
    }
    setSaving(false);
  };

  const handleSendNow = async () => {
    setSending(true);
    try {
      const res = await digestAPI.sendNow();
      toast.success(res.data.message || 'Digest sent!');
    } catch {
      toast.error('Failed to send digest');
    }
    setSending(false);
  };

  if (loading) return null;

  return (
    <Card data-testid="weekly-digest-settings">
      <CardHeader className="pb-3">
        <CardTitle className="text-base font-heading flex items-center gap-2">
          <Mail size={18} className="text-sky-500" />
          Weekly Performance Digest
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-4">
        <p className="text-sm text-slate-500">
          Receive a weekly email summarizing your downline's performance — new agents, contract activity, and open tickets.
        </p>
        <div className="flex items-center justify-between">
          <Label className="text-sm font-medium">Enable Weekly Digest</Label>
          <Switch
            checked={config.enabled}
            onCheckedChange={(val) => setConfig(prev => ({ ...prev, enabled: val }))}
            data-testid="digest-enabled-switch"
          />
        </div>
        {config.enabled && (
          <div className="flex items-center gap-3">
            <CalendarDays size={16} className="text-slate-400" />
            <Label className="text-sm">Send on:</Label>
            <Select
              value={String(config.day_of_week)}
              onValueChange={(v) => setConfig(prev => ({ ...prev, day_of_week: parseInt(v) }))}
            >
              <SelectTrigger className="w-40" data-testid="digest-day-select">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {DAYS.map((d, i) => (
                  <SelectItem key={i} value={String(i)}>{d}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        )}
        <div className="flex gap-2 pt-2">
          <Button size="sm" onClick={handleSave} disabled={saving} data-testid="digest-save-btn">
            {saving ? <Loader2 size={14} className="animate-spin mr-1" /> : null}
            Save Preferences
          </Button>
          <Button size="sm" variant="outline" onClick={handleSendNow} disabled={sending} data-testid="digest-send-now-btn">
            {sending ? <Loader2 size={14} className="animate-spin mr-1" /> : <Send size={14} className="mr-1" />}
            Send Now
          </Button>
        </div>
      </CardContent>
    </Card>
  );
}
